import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="relative mt-32 border-t border-white/5 bg-ink-950">
      <div className="container-x pt-24 pb-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-5">
            <p className="eyebrow mb-6">{t("footer.eyebrow")}</p>
            <h2 className="display-h2 max-w-xl">{t("footer.headline")}</h2>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-testid="footer-cta-contact">
                {t("common.startConversation")}
                <ArrowUpRight size={16} />
              </Link>
              <a
                href="mailto:sales@revgrow.co.uk"
                className="btn-ghost"
                data-testid="footer-cta-email"
              >
                sales@revgrow.co.uk
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-widest2 text-bone-200/40 mb-5">
              {t("footer.navigate")}
            </p>
            <ul className="space-y-3 text-sm text-bone-100/80">
              <li><Link to="/" className="hover:text-clay-400 transition-colors">{t("nav.home")}</Link></li>
              <li><Link to="/services" className="hover:text-clay-400 transition-colors">{t("nav.services")}</Link></li>
              <li><Link to="/case-studies" className="hover:text-clay-400 transition-colors">{t("nav.caseStudies")}</Link></li>
              <li><Link to="/about" className="hover:text-clay-400 transition-colors">{t("nav.about")}</Link></li>
              <li><Link to="/blog" className="hover:text-clay-400 transition-colors">{t("nav.blog")}</Link></li>
              <li><Link to="/contact" className="hover:text-clay-400 transition-colors">{t("nav.contact")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-widest2 text-bone-200/40 mb-5">
              {t("footer.services")}
            </p>
            <ul className="space-y-3 text-sm text-bone-100/80">
              <li><Link to="/services" className="hover:text-clay-400 transition-colors">{t("servicesData.goToMarket.title")}</Link></li>
              <li><Link to="/services" className="hover:text-clay-400 transition-colors">{t("servicesData.insights.title")}</Link></li>
              <li>
                <a
                  href="https://www.revgrow-services.co.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-clay-400 transition-colors inline-flex items-center gap-1"
                  data-testid="footer-link-demand-gen"
                >
                  {t("servicesData.abm.title")} <ArrowUpRight size={12} />
                </a>
              </li>
              <li><Link to="/services" className="hover:text-clay-400 transition-colors">{t("servicesData.media.title")}</Link></li>
              <li><Link to="/services" className="hover:text-clay-400 transition-colors">{t("servicesData.creative.title")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-widest2 text-bone-200/40 mb-5">
              {t("footer.contact")}
            </p>
            <ul className="space-y-4 text-sm text-bone-100/80">
              <li className="flex items-start gap-3">
                <Mail size={14} className="mt-1 text-clay-400 shrink-0" />
                <a href="mailto:sales@revgrow.co.uk" className="hover:text-clay-400 transition-colors">
                  sales@revgrow.co.uk
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={14} className="mt-1 text-clay-400 shrink-0" />
                <a href="tel:+493055235449" className="hover:text-clay-400 transition-colors">
                  +49 30 55235449
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={14} className="mt-1 text-clay-400 shrink-0" />
                <span>
                  RevGrow UG (haftungsbeschränkt)<br />
                  Akazienstraße 3A<br />
                  10823 Berlin, Germany
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-24 mb-4">
          <div
            aria-hidden
            className="font-display text-[24vw] leading-none tracking-tighter text-bone-100/[0.04] select-none pointer-events-none"
          >
            RevGrow
          </div>
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-bone-200/40">
          <p>© {new Date().getFullYear()} RevGrow UG (haftungsbeschränkt) · Berlin. {t("footer.rights")}</p>
          <div className="flex flex-wrap items-center gap-6 uppercase tracking-widest2">
            <Link to="/impressum" className="hover:text-clay-400 transition-colors" data-testid="footer-link-impressum">
              {t("footer.impressum")}
            </Link>
            <Link to="/privacy" className="hover:text-clay-400 transition-colors" data-testid="footer-link-privacy">
              {t("footer.privacy")}
            </Link>
            <span>{t("footer.tagline")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
